'use client'

import { MouseEvent, useEffect, useState } from 'react'
import { Cell, Icon } from '@vinted/web-ui'
import { PriceTag24, Calendar24 } from '@vinted/monochrome-icons'

import { SELLING_RESTRICTION_URL } from '@marketplace-web/user-restrictions/user-restrictions-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  OppositeUser,
  reserveTransactionItems,
  TransactionModel,
  ApplicableTransactionAction,
  AdditionalTransactionAction,
} from '@marketplace-web/messaging/conversation-messaging-data'

import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'

import ConfirmModal from '../ConfirmModal'
import { MARK_AS_SOLD_URL } from '../../constants/routes'

enum OrderActionModal {
  Reserve,
  RemoveReservation,
}

type Props = {
  transaction?: TransactionModel | null
  oppositeUser?: OppositeUser | null
  isSellingRestricted?: boolean
  onReserveToggle: () => void
}

const OrderActions = ({
  transaction,
  oppositeUser,
  isSellingRestricted = false,
  onReserveToggle,
}: Props) => {
  const translate = useTranslate('conversation.conversation_info')

  const [visibleModalName, setVisibleModalName] = useState<OrderActionModal | null>(null)
  const [isReserveInProgress, setIsReserveInProgress] = useState(false)

  const trackAbTest = useTrackAbTestCallback()
  const removeReserveAbTest = useAbTest('remove_reserve')
  const shouldRemoveReserveButton = removeReserveAbTest?.variant === 'on'

  useEffect(() => {
    if (
      !transaction?.availableActions ||
      transaction?.order?.reserved ||
      !transaction.availableActions.includes(ApplicableTransactionAction.Reserve)
    )
      return

    trackAbTest(removeReserveAbTest)
  }, [
    trackAbTest,
    removeReserveAbTest,
    transaction?.availableActions,
    transaction?.order?.reserved,
  ])

  if (!transaction?.availableActions) return null

  const { availableActions } = transaction

  const handleReserveClick = () => {
    setVisibleModalName(OrderActionModal.Reserve)
  }

  const handleReserveModalConfirmClick = async () => {
    if (!transaction || !oppositeUser || isReserveInProgress) return

    setIsReserveInProgress(true)

    await reserveTransactionItems(transaction.id, true)

    onReserveToggle()

    setIsReserveInProgress(false)

    setVisibleModalName(null)
  }

  const handleReserveModalCancelClick = () => {
    setVisibleModalName(null)
  }

  const removeItemReservation = async () => {
    if (!transaction?.order || isReserveInProgress) return

    setIsReserveInProgress(true)

    await reserveTransactionItems(transaction.id, false)

    onReserveToggle()

    setIsReserveInProgress(false)
  }

  const handleRemoveReservationModalConfirmClick = async () => {
    await removeItemReservation()

    setVisibleModalName(null)
  }

  const handleRemoveReservationModalCancelClick = () => {
    setVisibleModalName(null)
  }

  const handleReservationRemoval = (event: MouseEvent) => {
    event.preventDefault()

    setVisibleModalName(OrderActionModal.RemoveReservation)
  }

  const renderMarkAsSoldAction = () => {
    if (!availableActions.includes(AdditionalTransactionAction.MarkAsSold)) return null

    if (!transaction) return null

    return (
      <Cell
        title={translate('transaction_actions.mark_as_sold')}
        url={isSellingRestricted ? SELLING_RESTRICTION_URL : MARK_AS_SOLD_URL(transaction.id)}
        prefix={<Icon name={PriceTag24} color="primary-default" />}
        type="navigating"
        testId="mark-as-sold-action"
      />
    )
  }

  const renderRemoveReservationModal = () => {
    return (
      <ConfirmModal
        show={visibleModalName === OrderActionModal.RemoveReservation}
        theme="primary"
        translationsPrefix="conversation.conversation_info.remove_reservation_modal"
        onConfirm={handleRemoveReservationModalConfirmClick}
        onCancel={handleRemoveReservationModalCancelClick}
      />
    )
  }

  const renderReserveModal = () => {
    return (
      <ConfirmModal
        show={visibleModalName === OrderActionModal.Reserve}
        theme="primary"
        translationsPrefix="conversation.conversation_info.reserve_modal"
        onConfirm={handleReserveModalConfirmClick}
        onCancel={handleReserveModalCancelClick}
      />
    )
  }

  const renderReserveAction = () => {
    if (!availableActions.includes(ApplicableTransactionAction.Reserve)) return null

    if (!transaction) return null

    if (transaction.order?.reserved) {
      return (
        <>
          <Cell
            title={translate('transaction_actions.unreserve')}
            prefix={<Icon name={Calendar24} color="primary-default" />}
            onClick={handleReservationRemoval}
            type="navigating"
          />
          {renderRemoveReservationModal()}
        </>
      )
    }

    if (shouldRemoveReserveButton) return null

    return (
      <>
        <Cell
          title={translate('transaction_actions.reserve')}
          url={isSellingRestricted ? SELLING_RESTRICTION_URL : undefined}
          onClick={isSellingRestricted ? undefined : handleReserveClick}
          prefix={<Icon name={Calendar24} color="primary-default" />}
          type="navigating"
          testId="reserve-action"
        />
        {renderReserveModal()}
      </>
    )
  }

  return (
    <>
      {renderMarkAsSoldAction()}
      {renderReserveAction()}
    </>
  )
}

export default OrderActions
